import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useApi, toApiError, type UpdateUserAccessRequest } from '@/shared/api';
import { createAuthStore } from '@/shared/auth';
import { userKeys } from './queries';

/**
 * Benutzer-Mutationen (Deaktivieren/Reaktivieren/Invite erneut) gebündelt — spiegelt das
 * useBookingMutations-Pattern. Zugriff wird ausschließlich über die Benutzerakte geschrieben.
 */
export function useUserMutations(callbacks: {
  onDeactivated: () => void;
  onRenamed: () => void;
  onAccessUpdated: () => void;
  onTwoFactorReset: () => void;
}) {
  const { t } = useTranslation('accountRetention');
  const api = useApi();
  const queryClient = useQueryClient();

  // Prefix statt `lists()`: dieselben Mutationen laufen aus der Benutzerakte, deren Query unter
  // `detail(id)` liegt. Ohne den Prefix zeigte die Akte nach dem Speichern den alten Stand.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: userKeys.all });
  const onError = async (err: unknown) => {
    const e = await toApiError(err);
    toast.error(e.title === 'User.RetentionExpired' ? t('expired') : (e.detail ?? e.title));
  };

  const deactivate = useMutation({
    mutationFn: (id: string) => api.users.deactivate(id),
    onSuccess: async () => {
      await invalidate();
      toast.success('Benutzer deaktiviert');
      callbacks.onDeactivated();
    },
    onError,
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => api.users.reactivate(id),
    onSuccess: async () => {
      await invalidate();
      toast.success('Benutzer reaktiviert');
    },
    onError,
  });
  const resend = useMutation({
    mutationFn: (id: string) => api.users.resendInvitation(id),
    onSuccess: () => toast.success('Einladung erneut versendet'),
    onError,
  });
  const updateAccess = useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateUserAccessRequest }) =>
      api.users.updateAccess(id, request),
    onSuccess: async (_, variables) => {
      await invalidate();
      if (createAuthStore().getState().user?.userId === variables.id) {
        await createAuthStore().getState().hydrate();
      }
      toast.success('Zugriff gespeichert');
      callbacks.onAccessUpdated();
    },
    onError,
  });

  const rename = useMutation({
    mutationFn: ({
      id,
      firstName,
      lastName,
    }: {
      id: string;
      firstName: string;
      lastName: string;
    }) => api.users.update(id, { firstName, lastName }),
    onSuccess: async () => {
      await invalidate();
      toast.success('Name gespeichert');
      callbacks.onRenamed();
    },
    onError,
  });

  const resetTwoFactor = useMutation({
    mutationFn: ({ id, password, code }: { id: string; password: string; code: string }) =>
      api.users.resetTwoFactor(id, { password, code }),
    onSuccess: async () => {
      await invalidate();
      toast.success('Zwei-Faktor-Anmeldung zurückgesetzt');
      callbacks.onTwoFactorReset();
    },
    onError,
  });

  return { deactivate, reactivate, resend, updateAccess, rename, resetTwoFactor };
}
